import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Navigate } from "react-router-dom";
import { useChangeRole } from "../../hooks/useChangeRole";

import RemoteLayout from "./layout";
import { Button, Video } from "../../components/workshop";
import { WorkshopContext } from "../../contexts/workshop.context";
import { defaultPlatforms, defaultRoles } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute } from "../../util/general.util";
import ParticipantsStatus from "../../components/workshop/participantsStatus";

import { demoUsers } from "../../util/demoData.util";

const VideoPage = ({ demoWorkshop = false }) => {
  const { changeToDefaultRole } = useChangeRole();
  const [readyParticipants, setReadyParticipants] = useState([]);
  const { videoId } = useParams();
  const {
    socket,
    user: { id: userId, role } = {},
    workshopDetails: { platform } = {}
  } = useContext(WorkshopContext);

  const { t } = useTranslation();

  useEffect(() => {
    if (demoWorkshop) {
      setReadyParticipants(demoUsers);
    }
    if (platform !== defaultPlatforms.mtOnline) return;
    changeToDefaultRole();

    socket.on("videoReadyUsers", (users) => {
      if (users) setReadyParticipants(users);
    });

    socket.emit("videoReadyUsers", videoId);
  }, []);

  const isUserReady = readyParticipants.map((user) => user.id).includes(userId);

  const VideoIndex = parseInt(videoId);

  // Check if the VideoIndex is valid based on the platform
  const maxVideos = [
    defaultPlatforms.mt,
    defaultPlatforms.mtOnline,
    defaultPlatforms.mtDemo,
    defaultPlatforms.mtOnlineDemo
  ].includes(platform)
    ? 10
    : 6;

  const workshopProgress = [
    defaultPlatforms.mt,
    defaultPlatforms.mtOnline,
    defaultPlatforms.mtDemo
  ].includes(platform)
    ? VideoIndex / 10
    : VideoIndex / 6;

  if (isNaN(VideoIndex) || VideoIndex < 1 || VideoIndex > maxVideos) {
    return <Navigate to="/not-found" />;
  }

  const handleNext = () => {
    const nextRoute = getNextRoute(`/video/${VideoIndex}`, platform);

    socket.emit("navigate", nextRoute);
  };

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/video/${VideoIndex}`, platform);
    socket.emit("navigate", previousRoute);
  };

  const handleReadyClick = () => {
    if (demoWorkshop) {
      if (readyParticipants.length > demoUsers.length) return;

      setReadyParticipants([...readyParticipants, { userId, role }]);
    } else {
      socket.emit("videoReady", { userId, videoId });
    }
  };

  return (
    <RemoteLayout
      buttonText={t("buttonNext") + " →"}
      onButtonClick={role !== defaultRoles.participant && handleNext}
      onBackClick={VideoIndex !== 1 && role !== defaultRoles.participant && handleBack}>
      <Video videoId={videoId} workshopProgress={workshopProgress} isDark={true} />
      <div className="flex flex-col mt-4">
        <ParticipantsStatus
          heading={t("participantsStatus.heading")}
          readyParticipants={readyParticipants}
          badgeText={t("participantsStatus.badgeText")}
          isReady={isUserReady}
          isModerator={role === defaultRoles.moderator}
          onReadyClick={handleReadyClick}
          variant="2"
        />
      </div>
    </RemoteLayout>
  );
};

export default VideoPage;
